import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";

import { Logo } from "@/components/brand/Logo";
import { ButtonAnchor } from "@/components/ui/Button";
import { PrimaryCta } from "@/components/ui/PrimaryCta";
import { otherLocale, useLocale } from "@/i18n";
import { cn } from "@/lib/cn";
import { signInUrl } from "@/lib/platform-url";

function useNavLinks() {
  const { t, path } = useLocale();
  return [
    { to: path("/features"), label: t.nav.features },
    { to: path("/how-it-works"), label: t.nav.howItWorks },
    { to: path("/processing-modes"), label: t.nav.modes },
    { to: path("/security"), label: t.nav.security },
    { to: path("/pricing"), label: t.nav.pricing },
  ];
}

function LanguageSwitcher({ className }: { className?: string }) {
  const { locale, t } = useLocale();
  const { pathname, hash } = useLocation();
  const next = otherLocale(locale);

  // Preserve the current page when switching language: /ar/features -> /en/features
  const rest = pathname.replace(/^\/(ar|en)/, "");
  const target = `/${next}${rest}${hash}`;

  return (
    <Link
      to={target}
      hrefLang={next}
      aria-label={t.nav.switchLanguage}
      className={cn(
        "rounded-full border border-white/12 px-3.5 py-1.5 text-sm",
        "text-cream-100/75 transition-colors duration-300",
        "hover:border-white/25 hover:text-cream-50",
        className,
      )}
    >
      {next === "ar" ? "العربية" : "English"}
    </Link>
  );
}

export function Header() {
  const { t, locale, path } = useLocale();
  const links = useNavLinks();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 8,
  );
  const { pathname } = useLocation();

  /**
   * Close the mobile menu whenever the route changes.
   *
   * Adjusted during render rather than in an effect: an effect would run after
   * the new page had already painted with the menu still open, and would cost
   * an extra render pass. This is React's documented pattern for deriving
   * state from a changing value.
   */
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setOpen(false);
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition-colors duration-500",
        scrolled || open
          ? "border-b border-white/8 bg-ink-950/85 backdrop-blur-xl"
          : "border-b border-transparent",
      )}
    >
      <div className="container-page flex h-16 items-center justify-between gap-6 md:h-18">
        {/* The header is always a dark surface, so the light mark needs no plate. */}
        <Link
          to={path("/")}
          className="shrink-0"
          aria-label={t.brand.company}
        >
          <Logo size="md" alt={t.brand.logoAlt} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label={t.nav.features}>
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-3.5 py-2 text-sm transition-colors duration-300",
                  isActive
                    ? "text-cream-50"
                    : "text-cream-100/60 hover:text-cream-50",
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <LanguageSwitcher className="hidden sm:inline-flex" />
          {/* An anchor, not a <Link>: sign-in lives in the console, which is a
              different SPA on the same origin. A client-side route transition
              would look for a marketing page that no longer exists. */}
          <ButtonAnchor
            href={signInUrl(locale)}
            variant="ghost"
            className="hidden px-3 md:inline-flex"
          >
            {t.cta.signIn}
          </ButtonAnchor>
          <PrimaryCta size="md" className="hidden sm:inline-flex" />

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/12 text-cream-50 lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open ? (
        <div
          id="mobile-nav"
          className="border-t border-white/8 bg-ink-950/95 backdrop-blur-xl lg:hidden"
        >
          <div className="container-page flex flex-col gap-1 py-5">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-xl px-3 py-3 text-base transition-colors",
                    isActive
                      ? "bg-white/[0.05] text-cream-50"
                      : "text-cream-100/70 hover:text-cream-50",
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}

            <div className="mt-4 flex flex-col gap-3">
              <PrimaryCta size="lg" />
              <div className="flex items-center gap-3">
                <ButtonAnchor
                  href={signInUrl(locale)}
                  variant="secondary"
                  className="flex-1"
                >
                  {t.cta.signIn}
                </ButtonAnchor>
                <LanguageSwitcher className="px-4 py-2.5" />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
